<script>
    import {onMount, tick} from "svelte";

    import AssessmentNav from "./Components/AssessmentNav.svelte";
    import {confirm} from './Components/Confirmation.svelte';
    import Loader from './Components/Loader.svelte';
    import Assessment from './Models/assessmentModel';
    import AssessmentTypeModel from "./Models/assessmentTypeModel";

    import {notifier} from '@beyonk/svelte-notifications';

    import {forms} from './lib/forms';
    import {addFormModal} from './Forms/ModalFormManager.svelte';

    import { flatten } from "lodash-es";
    import findings_map from './lib/findings_library_map';

    import Library from './Models/LibraryORM';

    import {getUsers, userMap} from './Utilities/Session.svelte';

    import {db} from './Database.svelte';

    export let params;

    let assessment;
    let assessment_model;

    let questions = [];
    let collapsedCategory = {};
    let button_toggle = {};

    let user_choices = [];

    let loading = true;

    let importing = false;


    onMount(async () => {
        let assessment_details = await Assessment.getAssessmentDetails(params.id);

        assessment_model = new Assessment(assessment_details);

        user_choices = await getUsers();

        let unsub = assessment_model.store.subscribe((data) => {
            // data is undefined at start
            if (!data) return;

            // set assessment data
            assessment = data;

            // set questions based on assessment type
            questions = assessment.assessment_type_data.categories;

            // mark assessment as loaded
            loading = false;
        });

        return () => {
            unsub();
        }
    });

    function setAssessmentLevel(level) {
        assessment_model.setLevel(level);
    }

    function addNote(evt) {
        let note = evt.target.note;
        let color_class = evt.target.color_class.value;

        assessment_model.addScopeNote(note.value, color_class);

        evt.target.reset();
        note.focus();
    }

    function editNote(note) {
        addFormModal({
            fields: forms['scopeNote'],
            data: note,
            title: "Edit Scope Note",
            showDelete: true
        }).then(note => {
            assessment_model.updateScopeNote(note);
        }).catch(action => {
            if (action == "delete") {
                confirm(`Are you sure you want to delete "${note.note}?`).then(() => {
                    assessment_model.deleteScopeNote(note);
                });
            }
        });
    }

    async function refreshAssessmentType() {
        let assessment_type = (await AssessmentTypeModel.getAssessmentTypeStatic(assessment.details.assessment_type_id)).assessment_type_data;
        let assessment_type_data = await AssessmentTypeModel.flattenAssessmentType(assessment_type);

        try {
            await confirm("Are you sure you want to refresh? This cannot be undone.")
        } catch {
            return;
        }

        if (assessment.assessment_type_data._rev) {
            assessment.assessment_type_data = {
                _id: assessment.assessment_type_data._id,
                _rev: assessment.assessment_type_data._rev,
                ...assessment_type_data
            }

            assessment_model.saveAssessmentTypeData(assessment.assessment_type_data).then(() => {
                notifier.success("Assessment Type Refreshed");
            });
        } else {
            assessment.details.assessment_type_data = assessment_type_data;
            Assessment.saveDetails(assessment.details).then(() => {
                notifier.success("Assessment Type Refreshed");
            });
        }
}

    function addDomain(evt) {
        let textBox = evt.target.domain;

        assessment_model.addDomain(textBox.value);

        textBox.value = "";
        textBox.focus();
    }

    function deleteDomain(domain) {
        confirm(`Are you sure you want to delete "${domain}"?`).then(() => {
            assessment_model.deleteDomain(domain);
        }).catch(() => null);
    }

    function addQuestionTag(evt) {
        let textBox = evt.target.tag;
        let colorClass = evt.target.color_class;

        assessment_model.addQuestionTag(textBox.value, colorClass.value);

        evt.target.reset();
        textBox.focus();
    }

    function deleteQuestionTag(tag) {
        confirm(`Are you sure you want to delete "${tag.name}?`).then(() => {
            assessment_model.deleteQuestionTag(tag);
        }).catch(() => null)
    }

    function addCommentTag(evt) {
        let textBox = evt.target.tag;
        let colorClass = evt.target.color_class;

        assessment_model.addCommentTag(textBox.value, colorClass.value);

        evt.target.reset();
        textBox.focus();
    }

    function deleteCommentTag(tag) {
        confirm(`Are you sure you want to delete "${tag.name}?`).then(() => {
            assessment_model.deleteCommentTag(tag);
        }).catch(() => null)
    }

    function dragStart(evt, domain) {
        evt.dataTransfer.setData("Text", domain);
        evt.dataTransfer.setDragImage(evt.target, 0, 0);

        return true;
    }

    function dragEnter(evt, control) {
        evt.target.classList.add('has-background-primary-light');
        return false;
    }

    function dragLeave(evt, control) {
        evt.target.classList.remove('has-background-primary-light');
    }

    function dragDrop(evt, control) {
        let domain = evt.dataTransfer.getData("Text");

        assessment_model.mapDomain(control.id, domain);

        evt.target.classList.remove('has-background-primary-light');
    }

    async function subcategoryDragDrop(evt, subcategory) {
        let domain = evt.dataTransfer.getData("Text");

        assessment_model.bulkMap(subcategory.controls, [domain]);

        evt.target.classList.remove('has-background-primary-light');
    }

    function removeDomainFromControl(control, domain) {
        assessment_model.unmapDomain(control.id, domain)
    }

    function loadPrevious() {
        addFormModal({
            fields: [
                {
                    name: "assessment_name",
                    label: "Previous Assessment Name",
                    type: "text",
                    required: true
                },
                {
                    name: 'previous',
                    type: 'file',
                    label: "Previous Assessment JSON",
                    required: true,
                    fieldProps: {
                        accept: 'application/json'
                    }
                }
            ]
        }).then(async (data) => {
            const file = data.previous[0];

            // make sure file is json
            if (file.type != 'application/json') {
                notifier.danger("Invalid File Type")
                return;
            }

            let assessment_json = await file.text();
            try {
                var prev_assessment = JSON.parse(assessment_json)
            } catch (e) {
                notifier.danger("Could not parse JSON file");
                return;
            }

            if (prev_assessment.details.type != "assessment") {
                notifier.danger("JSON must be Assessment data")
                return
            }

            let fields = [];
            let previous_assessment_libraries;
            let current_assessement_libraries = assessment.assessment_type_data.report_sections.filter(s => s.section_type == "library");

            if (prev_assessment.assessment_type_data.report_sections && prev_assessment.assessment_type_data.report_sections.length) {
                previous_assessment_libraries =  prev_assessment.assessment_type_data.report_sections.filter(s => ["library", "findings"].includes(s.section_type));
            } else {
                previous_assessment_libraries = [{
                    section_type: 'findings',
                    section_name: 'Recommendations'
                }];
            }

            // check for assessment type match
            if (!(prev_assessment.details.assessment_type_id == assessment.details.assessment_type_id)) {
                // check if mapping exists
                let mappings = await db.find({
                    selector: {
                        type: "RecurringAssessmentMapping",
                        from: prev_assessment.details.assessment_type_id,
                        to: assessment.details.assessment_type_id,
                    }
                });

                if (mappings.docs.length == 0) {
                    notifier.danger(`No mappings found from "${prev_assessment.details.assessment_type}" to "${assessment.details.assessment_type}".`)
                    return;
                }

                fields.push({
                    name: "control_mapping",
                    label: "Control Mapping",
                    type: "select",
                    required: true,
                    choices: {
                        static: mappings.docs.map((m) => {
                            return {
                                value: m._id,
                                label: m.name
                            }
                        })
                    }
                })
            }

            let previous_section_map = {};

            for (let section of previous_assessment_libraries) {
                previous_section_map[section.section_name] = section;

                let section_choices = current_assessement_libraries.filter((s) => {
                    if (section.section_type == "findings") {
                        return true
                    }

                    return s.library_type == section.library_type;
                })

                if (section_choices.length) {
                    fields.push({
                        name: section.section_name,
                        label: section.section_name,
                        type: "select",
                        multiple: true,
                        choices: {
                            static: section_choices.map(s => {return {label: s.section_name, value: s}})
                        }
                    })
                }
            }

            addFormModal({
                title: "Map Library Sections",
                fields
            }).then(async (mappings) => {
                importing = "Importing...";

                await tick();

                importing = "Loading section mappings"
                for (let key of Object.keys(mappings)) {
                    // skip control mappings
                    if (key == "control_mapping") continue

                    if (!mappings[key]) continue;

                    let section_type = previous_section_map[key].section_type;


                    if (section_type == "library") {

                        for (let section of mappings[key]) {

                            // check if report_data exists
                            if (!prev_assessment.report_data[key]) {continue};

                            importing = `Loading library ${key}`
                            let library_fields = await Library.getFieldData(section.library_type);
                            let promises = [];

                            for (let item of prev_assessment.report_data[key]) {
                                let sectionItem = {};
                                for (let field of library_fields.formFields) {
                                    sectionItem[field.name] = item[field.name] || field.dflt || "";
                                }

                                if (item["tags_"]) {
                                    sectionItem["tags_"] = item['tags_']
                                }

                                if (item.template_id) {
                                    sectionItem.template_id = item.template_id
                                }

                                promises.push(assessment_model.saveLibraryItem(section, sectionItem));
                            }

                            await Promise.all(promises);
                        }
                    } else if (section_type == "findings") {
                        let findings = flatten(Object.values(prev_assessment.recommendations));
                        for (let section of mappings[key]) {

                            let section_fields = await Library.getFieldData(section.library_type);
                            for (let finding of findings) {

                                // mapping for old findings type
                                let newFinding = {};
                                for (let finding_key of Object.keys(findings_map)) {
                                    newFinding[findings_map[finding_key]] = finding[finding_key];
                                }

                                // get new section fields
                                let sectionItem = {};

                                // map to section fields
                                for (let field of [...section_fields.formFields, ...section.extra_fields]) {
                                    sectionItem[field.name] = newFinding[field.name] || field.dflt || "";
                                }

                                await assessment_model.saveLibraryItem(section, sectionItem);
                            }
                        }
                    }
                }
                assessment_model.loadPreviousResults(data.assessment_name, prev_assessment, mappings['control_mapping']).then(() => {
                    notifier.success("Previous Assessment Data Loaded");
                    importing = false;
                }).catch((err) => {
                    console.error(err);
                    notifier.danger("Error Loading Previous Results");
                    importing = false;
                });
            });

        })
    }

    function addUser(evt) {
        let form = evt.target;
        let user = form.user.value;

        assessment_model.addUser(user).catch((e) => {
            console.error(e);
            notifier.danger("Error Adding User")
        });
    }
</script>

<Loader {loading}>
    <div class="container viewport is-fluid">
        <div class="columns viewport">
            <div class="column is-3">
                <AssessmentNav assessment_id={assessment.details._id} />
                <!--
                <p class="panel-tabs">
                    <a class:is-active={isActive(`#/assessment/${assessment.details._id}/scope`)} href="#/assessment/{assessment.details._id}/scope">Scoping</a>
                    <a class:is-active={isActive(`#/assessment/${assessment.details._id}`)} href="#/assessment/{assessment.details._id}">Assessment</a>
                    <a class:is-active={isActive(`#/assessment/${assessment.details._id}/results`)} href="#/assessment/{assessment.details._id}/results">Results</a>
                    <a class:is-active={isActive(`#/assessment/${assessment.details._id}/review`)} href="#/assessment/{assessment.details._id}/review">Review</a>
                </p>
                -->
                <h1 class="title is-1">
                    {assessment.details.name}
                </h1>
                <div class="buttons">
                    <span on:click={refreshAssessmentType} class="button is-warning">Refresh Assessment Type</span>

                    <span on:click={loadPrevious} class="button is-primary">Load Previous Assessment</span>
                </div>
                <h2 class="title is-2">Scope Notes</h2>
                {#each assessment.scope_notes as note}
                    <article class="media">
                        <div class="media-content">
                            <div class="content">
                                <p class="is-size-6">
                                    <strong>{note.user}</strong> <small>{note.timestamp}</small>
                                    <span class="icon pointer" on:click={() => editNote(note)}>
                                        <i>✏️</i>
                                    </span>
                                    <br>
                                    <span class="tag is-medium is-{note.color_class}">{note.note}</span>
                                </p>
                            </div>
                        </div>
                    </article>
                    <hr>
                {/each}
                <form on:submit|preventDefault={addNote}>
                    <div class="field has-addons">
                        <p class="control">
                            <span class="select">
                                <select name="color_class">
                                    <option value="info">Blue</option>
                                    <option value="success">Green</option>
                                    <option value="warning">Yellow</option>
                                    <option value="danger">Red</option>
                                </select>
                            </span>
                        </p>
                        <div class="control is-expanded">
                            <input class="input" name="note" type="text" placeholder="Scope Note" required>
                        </div>
                        <div class="control">
                            <button class="button is-info">
                                Add Scope Note
                            </button>
                        </div>
                    </div>
                </form>

                <h2 class="title is-2">Assigned Users</h2>

                <div class="tags">
                    {#each assessment.details.users as user}
                        <span class="tag">{$userMap[user] || user}</span>
                    {/each}
                </div>

                <form on:submit|preventDefault={addUser}>
                    <div class="field has-addons users">
                        <p class="control is-expanded">
                            <span class="select">
                                <select name="user">
                                    {#each user_choices as user}
                                        <option value={user.value}>{user.label}</option>
                                    {/each}
                                </select>
                            </span>
                        </p>
                        <div class="control">
                            <button class="button is-info">
                                Add User
                            </button>
                        </div>
                    </div>
                </form>
            </div>
            <div class="column">
                <!-- Assessment Level -->
                <h2 class="title is-2">Assessment Level</h2>
                <div class="buttons has-addons">
                    <button class="button" class:is-link={assessment.details.level == 1} on:click={() => setAssessmentLevel(1)}>Level 1</button>
                    <button class="button" class:is-link={assessment.details.level == 2} on:click={() => setAssessmentLevel(2)}>Level 2</button>
                    <button class="button" class:is-link={assessment.details.level == 3} on:click={() => setAssessmentLevel(3)}>Level 3</button>
                    <button class="button" class:is-link={assessment.details.level == 4} on:click={() => setAssessmentLevel(4)}>Level 4</button>
                    <button class="button" class:is-link={assessment.details.level == 5} on:click={() => setAssessmentLevel(5)}>Level 5</button>
                </div>

                <!-- Scope Domain Form -->
                <h2 class="title is-2">Scope Domains</h2>
                <form on:submit|preventDefault={addDomain}>
                    <div class="field has-addons">
                        <div class="control is-expanded">
                            <input class="input" name="domain" type="text" placeholder="Scope Domain" required>
                        </div>
                        <div class="control">
                            <button class="button is-info">
                                Add Domain
                            </button>
                        </div>
                    </div>
                </form>

                <!-- Scope Domains -->
                <div class="content fields">
                    {#each assessment.domains.names as domain}
                        <div class="field has-addons">
                            <p class="control draggable" draggable="true"  on:dragstart={(evt) => dragStart(evt, domain)}>
                                <span class="button is-light">{domain}</span>
                            </p>
                            <p class="control">
                                <button class="button is-light" on:click={() => deleteDomain(domain)}>
                                    X
                                </button>
                            </p>
                        </div>
                    {/each}
                </div>

                <!-- Question Tag Form -->

                <h2 class="title is-2">Question Tags</h2>

                <div class="fields">
                    {#if assessment.assessment_type_data.question_tags}
                        {#each assessment.assessment_type_data.question_tags as tag}
                            <div class="field">
                                <p class="control">
                                    <button
                                        class="button is-{tag.color_class || 'info'}"
                                        class:is-light={!button_toggle[tag.name]}
                                        on:click={() => button_toggle[tag.name] = !button_toggle[tag.name]}
                                    >
                                        {tag.name}
                                    </button>
                                </p>
                            </div>
                        {/each}
                    {/if}
                </div>
                <form on:submit|preventDefault={addQuestionTag}>
                    <div class="field has-addons">
                        <p class="control">
                            <span class="select">
                                <select name="color_class">
                                    <option value="info">Blue</option>
                                    <option value="success">Green</option>
                                    <option value="warning">Yellow</option>
                                    <option value="danger">Red</option>
                                </select>
                            </span>
                        </p>
                        <div class="control is-expanded">
                            <input class="input" name="tag" type="text" placeholder="Question Tag" required>
                        </div>
                        <div class="control">
                            <button class="button is-info">
                                Add Question Tag
                            </button>
                        </div>
                    </div>
                </form>

                <!-- Question Tags -->
                <div class="content fields">

                    {#each assessment.question_tags.tags as tag}
                        <div class="field has-addons">
                            <p class="control">
                                <button class="button is-{tag.color_class || 'info'}" class:is-light={!button_toggle[tag.name]} on:click={() => button_toggle[tag.name] = !button_toggle[tag.name]}>{tag.name}</button>
                            </p>
                            <p class="control">
                                <button class="button is-light" on:click={() => deleteQuestionTag(tag)}>
                                    X
                                </button>
                            </p>
                        </div>
                    {/each}
                </div>

                <!-- Comment Tag Form -->
                <h2 class="title is-2">Comment Tags</h2>
                <div class="fields">
                    {#if assessment.assessment_type_data.comment_tags}
                        {#each assessment.assessment_type_data.comment_tags as tag}
                            <div class="field has-addons">
                                <p class="control">
                                    <button class="button is-{tag.color_class || 'info'}" class:is-light={!button_toggle[tag.name]} on:click={() => button_toggle[tag.name] = !button_toggle[tag.name]}>{tag.name}</button>
                                </p>
                            </div>
                        {/each}
                    {/if}
                </div>

                <form on:submit|preventDefault={addCommentTag}>
                    <div class="field has-addons">
                        <p class="control">
                            <span class="select">
                                <select name="color_class">
                                    <option value="info">Blue</option>
                                    <option value="success">Green</option>
                                    <option value="warning">Yellow</option>
                                    <option value="danger">Red</option>
                                </select>
                            </span>
                        </p>
                        <div class="control is-expanded">
                            <input class="input" name="tag" type="text" placeholder="Comment Tag" required>
                        </div>
                        <div class="control">
                            <button class="button is-info">
                                Add Comment Tag
                            </button>
                        </div>
                    </div>
                </form>
                <!-- Comment Tags -->
                <div class="content fields">

                    {#each assessment.comment_tags.tags as tag}
                        <div class="field has-addons">
                            <p class="control">
                                <button class="button is-{tag.color_class || 'info'}" class:is-light={!button_toggle[tag.name]} on:click={() => button_toggle[tag.name] = !button_toggle[tag.name]}>{tag.name}</button>
                            </p>
                            <p class="control">
                                <button class="button is-light" on:click={() => deleteCommentTag(tag)}>
                                    X
                                </button>
                            </p>
                        </div>
                    {/each}
                </div>
            </div>

            <div class="column">
                <h2 class="title is-2">Control Assignment</h2>
                <aside class="menu">
                    <ul class="menu-list">
                        {#each questions as category}
                            <li>
                                <input type="checkbox" class="collapse" id="{category.control_number}" bind:checked={collapsedCategory[category.control_number]}>
                                <label for={category.control_number}>
                                    {category.name} ({category.control_number})
                                </label>
                                {#if collapsedCategory[category.control_number]}
                                <ul>
                                {#each category.subcategories as subcategory}
                                    <li class="drop"
                                        class:has-background-primary-light={false}
                                        on:dragenter|self={(evt) => dragEnter(evt, subcategory)}
                                        on:dragleave|self={dragLeave}
                                        on:dragover|preventDefault={()=>  false}
                                        on:drop|self|stopPropagation|preventDefault={evt => subcategoryDragDrop(evt, subcategory)}
                                    >
                                        <span><b>{category.control_number}.{subcategory.control_number}</b> {subcategory.name}</span>
                                        <ul>
                                            {#each subcategory.controls as control}
                                                <li>
                                                    <div
                                                        class:has-background-primary-light={false}
                                                        class="menu-item link drop"
                                                        on:dragenter|self|stopPropagation={(evt) => dragEnter(evt, control)}
                                                        on:dragover|self|preventDefault={()=>  false}
                                                        on:dragleave|self|capture|stopPropagation={dragLeave}
                                                        on:drop|stopPropagation|preventDefault={(evt) => dragDrop(evt,control)}
                                                    >
                                                        <span>
                                                            <b>{control.control_number}</b>
                                                            {control.text}
                                                        </span>
                                                        {#if (assessment.domains.mapping[control.id] || []).length }
                                                        <div class="field is-grouped is-grouped-multiline">
                                                            {#each assessment.domains.mapping[control.id] as domain}
                                                            <div class="control">
                                                                <div class="tags has-addons">
                                                                    <span class="tag">{domain}</span>
                                                                    <div class="tag is-delete" on:click={() =>removeDomainFromControl(control, domain) }></div>
                                                                </div>
                                                            </div>
                                                            {/each}
                                                        </div>
                                                        {/if}
                                                    </div>
                                                </li>
                                            {/each}
                                        </ul>
                                    </li>
                                {/each}
                                </ul>
                                {/if}
                            </li>
                        {/each}
                    </ul>
                </aside>
            </div>
        </div>
    </div>
</Loader>
<Loader loading={importing} message={importing} />

<style>
    .draggable {
        cursor: grab;
        user-select: none;
    }

    .drop > span, .drop.has-background-primary-light > div {
        pointer-events: none;
    }
    .column {
		overflow: auto;
	}

    .fields {
        display: flex;
        gap: 0.75rem;
        flex-wrap: wrap;
        margin-top: .75rem;
    }

    .field.users .select, .field.users select {
        width: 100%;
    }

    form {
        margin-top: .75rem;
    }
</style>